export type EventColor = "blue" | "plum" | "amber";
export type EventStatus = "inquiry" | "confirmed" | "completed" | "cancelled";
export type WorkClassification = "advance" | "final_prep" | "on_site" | "reset";
export type TaskStatus = "eligible" | "scheduled" | "in_progress" | "blocked" | "complete";
export type AttentionKind = "blocker" | "change" | "overdue";

export interface StaffMember {
  id: string;
  name: string;
  initials: string;
}

export interface EventContact {
  id: string;
  name: string;
  role: string;
  phone?: string;
  external: boolean;
}

export interface KeyTime {
  id: string;
  label: string;
  timeLabel: string;
}

export interface EventFlowStep {
  id: string;
  label: string;
  timeLabel: string;
  detail: string;
}

export interface EventCommitment {
  id: string;
  title: string;
  detail: string;
}

export interface EventRequirement {
  id: string;
  title: string;
  detail: string;
  critical: boolean;
  resolved: boolean;
}

export interface EventCondition {
  id: string;
  title: string;
  detail: string;
  severity: "info" | "caution" | "blocking";
}

/** Traceability for Events created from legacy invoice import during migration. */
export interface EventInvoiceSource {
  importCandidateId: string;
  sourceKind: "catalog" | "upload";
  filename: string;
  sourceUrl?: string;
  invoiceNumber?: string;
  invoiceIssueDate?: string;
  importedAt: string;
  /** Reviewed draft snapshot including management corrections. */
  reviewedDraft: Record<string, unknown>;
}

export interface OperationEvent {
  id: string;
  name: string;
  type: string;
  status: EventStatus;
  color: EventColor;
  client: string;
  dateLabel: string;
  dateRangeLabel?: string;
  location?: string;
  headcount?: number;
  selectedService?: string;
  leadStaffId?: string;
  contacts: EventContact[];
  keyTimes: KeyTime[];
  flow: EventFlowStep[];
  commitments: EventCommitment[];
  requirements: EventRequirement[];
  conditions: EventCondition[];
  /** Present when this Event was created from invoice import. */
  sourceInvoice?: EventInvoiceSource;
}

export interface WorkTask {
  id: string;
  eventId: string;
  blockId: string;
  title: string;
  detail: string;
  classification: WorkClassification;
  status: TaskStatus;
  scheduledTime?: string;
  assignedTo?: string;
  blockerNote?: string;
  /** Activity label such as Load — not a work classification. */
  activityLabel?: string;
}

export interface WorkBlock {
  id: string;
  label: "NOW" | "NEXT" | "LATER" | "FLEX";
  time: string;
  meridiem?: string;
  title: string;
  timeframe: string;
  location: string;
  icon: "kitchen" | "transport" | "venue" | "reset";
  taskIds: string[];
  staffIds: string[];
}

export interface AttentionItem {
  id: string;
  eventId: string;
  kind: AttentionKind;
  title: string;
  detail: string;
  timeLabel: string;
  resolved: boolean;
}

export interface OperationsSnapshot {
  events: OperationEvent[];
  staff: StaffMember[];
  tasks: WorkTask[];
  blocks: WorkBlock[];
  attention: AttentionItem[];
}

export const eventStatusLabels: Record<EventStatus, string> = {
  inquiry: "Inquiry",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const workClassLabels: Record<WorkClassification, string> = {
  advance: "Advance",
  final_prep: "Final Prep",
  on_site: "On-Site",
  reset: "Reset",
};

export const taskStatusLabels: Record<TaskStatus, string> = {
  eligible: "Eligible",
  scheduled: "Scheduled",
  in_progress: "In progress",
  blocked: "Blocked",
  complete: "Complete",
};

export const workClassifications: WorkClassification[] = ["advance", "final_prep", "on_site", "reset"];
